import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { message } from 'antd';
import { anomalyDocumentType } from '../../../utils/anomalyDocumentType';
import {
  pwd216Api,
  PendingItem,
  AnomalyDetail,
  AnomalyCodeInfo,
  ApproveItem,
} from '../../../services/pwd216Api';

export interface UsePwd216ApprovalReturn {
  // Selection
  checkedKeys: string[];
  setCheckedKeys: (keys: string[]) => void;
  selectedDocNo: string | null;
  setSelectedDocNo: (key: string | null) => void;

  // Local editable state (mirrors items but with edits)
  editableItems: PendingItem[];
  setEditableItems: (items: PendingItem[]) => void;

  // Detail editing for selected item
  updateAnomalyDesc: (docNo: string, desc: string) => void;
  addDetailRow: (docNo: string) => void;
  deleteDetailRow: (docNo: string, lineNo: number) => void;
  updateDetailField: (docNo: string, lineNo: number, field: keyof AnomalyDetail, value: string | number | null) => void;
  lookupAnomalyCode: (docNo: string, lineNo: number, code: string) => Promise<void>;

  // Approve
  approving: boolean;
  handleApprove: (onDone: () => Promise<void>) => Promise<void>;

  // Computed
  selectedItem: PendingItem | undefined;
}

export function usePwd216Approval(items: PendingItem[]): UsePwd216ApprovalReturn {
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const [selectedDocNo, setSelectedDocNo] = useState<string | null>(null);
  const [editableItems, setEditableItems] = useState<PendingItem[]>([]);
  const [approving, setApproving] = useState(false);
  const allocatedLineNos = useRef<Record<string, number>>({});
  useEffect(() => {
    allocatedLineNos.current = Object.fromEntries(items.map(item => [
      item.doc_no, Math.max(0, ...item.details.map(detail => detail.line_no)),
    ]));
  }, [items]);


  // Sync editableItems when items change from query
  // The parent will call setEditableItems after fetch
  const selectedItem = useMemo(
    () => editableItems.find((it) => it.doc_no === selectedDocNo),
    [editableItems, selectedDocNo],
  );

  const updateAnomalyDesc = useCallback((docNo: string, desc: string) => {
    setEditableItems((prev) =>
      prev.map((it) => (it.doc_no === docNo ? { ...it, anomaly_desc: desc } : it)),
    );
  }, []);

  const addDetailRow = useCallback((docNo: string) => {
    // Keep identities stable until the server atomically renumbers on save.
    // Include original and previously allocated rows even after local deletion.
    const original = items.find(item => item.doc_no === docNo);
    const current = editableItems.find(item => item.doc_no === docNo);
    const lineNo = Math.max(
      allocatedLineNos.current[docNo] || 0,
      ...((original?.details || []).map(detail => detail.line_no)),
      ...((current?.details || []).map(detail => detail.line_no)),
    ) + 1;
    allocatedLineNos.current[docNo] = lineNo;
    setEditableItems((prev) =>
      prev.map((it) => {
        if (it.doc_no !== docNo) return it;
        const newDetail: AnomalyDetail = {
          doc_no: docNo,
          line_no: lineNo,
          anomaly_code: '',
          desc: '',
          sched_date: null,
          doc_type_code: '',
          seq_no: 0,
        };
        return { ...it, details: [...it.details, newDetail] };
      }),
    );
  }, [items, editableItems]);

  const deleteDetailRow = useCallback((docNo: string, lineNo: number) => {
    setEditableItems((prev) =>
      prev.map((it) => {
        if (it.doc_no !== docNo) return it;
        const filtered = it.details.filter((d) => d.line_no !== lineNo);
        return { ...it, details: filtered };
      }),
    );
  }, []);

  const updateDetailField = useCallback(
    (docNo: string, lineNo: number, field: keyof AnomalyDetail, value: string | number | null) => {
      setEditableItems((prev) =>
        prev.map((it) => {
          if (it.doc_no !== docNo) return it;
          return {
            ...it,
            details: it.details.map((d) =>
              d.line_no === lineNo ? { ...d, [field]: value } : d,
            ),
          };
        }),
      );
    },
    [],
  );

  const lookupAnomalyCode = useCallback(
    async (docNo: string, lineNo: number, code: string) => {
      try {
        const info: AnomalyCodeInfo = await pwd216Api.lookupAnomalyCode(code);
        setEditableItems((prev) =>
          prev.map((it) => {
            if (it.doc_no !== docNo) return it;
            return {
              ...it,
              details: it.details.map((d) =>
                d.line_no === lineNo && d.anomaly_code.trim() === code.trim()
                  ? {
                    ...d,
                    anomaly_code: info.code,
                    desc: info.desc,
                    seq_no: info.doc_type,
                    doc_type_code: anomalyDocumentType(info.doc_type),
                  }
                  : d,
              ),
            };
          }),
        );
      } catch {
        // silent — user can still type manually
      }
    },
    [],
  );

  const handleApprove = useCallback(
    async (onDone: () => Promise<void>) => {
      if (checkedKeys.length === 0) {
        message.warning('請至少勾選一筆單據');
        return;
      }
      setApproving(true);
      try {
        const approveItems: ApproveItem[] = editableItems
          .filter((it) => checkedKeys.includes(it.doc_no))
          .map((it) => ({
            doc_no: it.doc_no,
            doc_type: it.doc_type,
            anomaly_desc: it.anomaly_desc,
            approved: true,
            details: it.details,
            // Only persisted IDs can be deleted. Removing a new unsaved row
            // must not ask the backend to delete a row that never existed.
            deleted_line_nos: (items.find(original => original.doc_no === it.doc_no)?.details || [])
              .filter(original => !it.details.some(detail => detail.line_no === original.line_no))
              .map(original => original.line_no),
          }));

        const resp = await pwd216Api.approve(approveItems);

        if (resp.errors && resp.errors.length > 0) {
          message.warning(`核准 ${resp.approved_count} 筆，${resp.errors.length} 筆錯誤: ${resp.errors.join('; ')}`);
        } else {
          message.success(`已核准 ${resp.approved_count} 筆`);
        }

        setCheckedKeys([]);
        setSelectedDocNo(null);
        await onDone();
      } catch (err: unknown) {
        const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
        message.error(msg);
      } finally {
        setApproving(false);
      }
    },
    [checkedKeys, editableItems, items],
  );

  return {
    checkedKeys,
    setCheckedKeys,
    selectedDocNo,
    setSelectedDocNo,
    editableItems,
    setEditableItems,
    updateAnomalyDesc,
    addDetailRow,
    deleteDetailRow,
    updateDetailField,
    lookupAnomalyCode,
    approving,
    handleApprove,
    selectedItem,
  };
}
